import { el, toast } from "./dom";
import { icon } from "./icons";

// Parts every record screen uses (brief 1B-1, moved here for 1B-2): the mockup's small table, an
// icon button with its hover tip, chips, a card with its head, and a file download.

/** A small table (mockup table() inside .tw); a row with `open` opens on click or Enter. */
export function table<T>(
  cols: { h: string; f: (r: T) => Node | string; num?: boolean }[],
  rows: readonly T[],
  open?: (r: T) => void,
  label?: string,
): HTMLElement {
  return el(
    "div",
    { class: "tw" },
    el(
      "table",
      {},
      label ? el("caption", { class: "sr" }, label) : null,
      el(
        "thead",
        {},
        el(
          "tr",
          {},
          ...cols.map((c) => el("th", { scope: "col", class: c.num ? "num" : undefined }, c.h)),
        ),
      ),
      el(
        "tbody",
        {},
        ...rows.map((r) => {
          const tr = el(
            "tr",
            { class: open ? "go" : undefined, tabindex: open ? "0" : undefined },
            ...cols.map((c) => el("td", { class: c.num ? "num" : undefined }, c.f(r))),
          );
          if (open) {
            tr.addEventListener("click", (e) => {
              if ((e.target as Element).closest("button,a,input,label")) return;
              open(r);
            });
            tr.addEventListener("keydown", (e) => {
              if (e.target === tr && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                open(r);
              }
            });
          }
          return tr;
        }),
      ),
    ),
  );
}

/** An icon button with its hover tip (mockup ib). */
export function ib(name: string, label: string, run: () => void): HTMLButtonElement {
  const b = el(
    "button",
    { type: "button", class: "btn ib sm", "aria-label": label, "data-tip": label },
    icon(name),
  );
  b.addEventListener("click", run);
  return b;
}

export const chip = (text: string, level = "plain") => el("span", { class: `chip ${level}` }, text);
export const muted = (text: string) => el("span", { class: "muted" }, text);

/** A card with its head: the title, a short line under it, and the actions on the right. */
export function card(
  title: Node | string,
  line: string | null,
  actions: Node[],
  ...body: Node[]
): HTMLElement {
  return el(
    "section",
    { class: "card" },
    el(
      "div",
      { class: "cardhead" },
      el("div", {}, el("h2", {}, title), line ? el("span", { class: "small muted" }, line) : null),
      el("div", { class: "rowacts" }, ...actions),
    ),
    ...body,
  );
}

/** Downloads a record's file, part by part (files over 4 MB come in parts). */
export async function download(id: string, name: string) {
  const parts: ArrayBuffer[] = [];
  let total = 1;
  for (let n = 0; n < total; n++) {
    const res = await fetch(`/api/files/${id}?part=${n}`, { cache: "no-store" });
    if (!res.ok) return toast("That file couldn't be downloaded");
    total = Number(res.headers.get("x-file-parts") ?? "1");
    parts.push(await res.arrayBuffer());
  }
  const a = el("a", { href: URL.createObjectURL(new Blob(parts)), download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
